<template>
    <main class="relative pt-25 md:pt-40 lg:mt-25 lg:pt-10">
        <TheBreadcrumbs
            v-if="isCromi()"
            class="mb-5"
            :items="breadcrumbs" />

        <section>
            <AtomTitle
                level="1"
                class="container lg:hidden">
                Портфолио
            </AtomTitle>
            <div
                class="container hidden items-center justify-between gap-5 rounded-20 bg-main-section px-20 pb-10 max-lg:pb-8 max-lg:pt-36 lg:flex lg:py-15">
                <div class="max-w-[21.5rem] shrink-0">
                    <h1
                        class="text-32/100 font-semibold tracking-tighter lg:text-48/100">
                        Портфолио
                    </h1>
                    <slot name="description" />
                </div>
                <div class="max-w-[49.75rem]">
                    <LazyApiSection
                        min-height="18rem"
                        @visible="loadDeferredPortfolioContent">
                        <InteractiveMap
                            v-if="maps && maps.data.length"
                            size="medium"
                            :regions="maps.data" />
                    </LazyApiSection>
                </div>
            </div>
        </section>

        <section
            class="container mt-10 flex flex-col justify-between gap-25 lg:flex-row lg:gap-10">
            <div class="shrink-0 max-md:w-full md:basis-1/4">
                <p class="text-16/120 font-semibold">Локация проекта:</p>
                <AtomDropdown
                    v-model="selectedRegion"
                    :options="dropdownOptions"
                    class="mt-3 w-full"
                    @update:model-value="
                        l5UpdateQuery({
                            region_id: l5Filters.region_id,
                            page: 1,
                        })
                    " />
            </div>
            <div class="hidden w-full items-end justify-center pb-2 md:flex">
                <SelectYearPeriod
                    v-model:year-from="l5Filters.year_from"
                    v-model:year-to="l5Filters.year_to"
                    :disabled-years="disabledYears"
                    :years="years"
                    @update:model-value="
                        (value) =>
                            l5UpdateQuery({
                                year_from: value[0],
                                year_to: value[1],
                                page: 1,
                            })
                    " />
            </div>
        </section>

        <CategoryMultiselect
            v-if="data?.data.all_categories?.data?.length"
            v-model="l5Filters.categories"
            :categories="data.data.all_categories.data"
            class="mt-10"
            @update:model-value="
                l5UpdateQuery({
                    categories: l5Filters.categories,
                    page: 1,
                })
            " />

        <div ref="scrollTarget"></div>

        <LazyApiSection
            min-height="32rem"
            @visible="loadDeferredPortfolioContent">
            <Transition
                name="fast-fade"
                mode="out-in">
                <section
                    v-if="!portfolios?.data.length && !pending"
                    class="container relative mt-10 flex items-center justify-center overflow-hidden rounded-20 py-40">
                    <div
                        class="relative space-y-7 text-center text-24/133 font-semibold">
                        <p>
                            Таких проектов у нас еще нет, но есть много других
                            интересных.
                        </p>
                        <p>Измените параметры фильтров и попробуйте еще раз</p>
                    </div>
                    <div class="absolute inset-0 size-full">
                        <div class="bg-noise"></div>
                        <div
                            class="absolute left-0 top-0 aspect-square w-1/3 -translate-x-1/2 -translate-y-1/2 animate-slow-pulse rounded-full bg-accent/60 blur-[6rem] delay-300" />
                        <div
                            class="absolute bottom-0 right-0 aspect-square w-1/3 translate-x-1/2 translate-y-1/2 animate-slow-pulse rounded-full bg-accent/50 blur-[6rem] delay-500" />
                    </div>
                </section>
                <section
                    v-else-if="portfolios?.data.length && !pending"
                    class="relative mt-10">
                    <div
                        class="container grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                        <PortfolioCard
                            v-for="item in portfolios?.data"
                            :key="item.slug"
                            :portfolio="item" />
                    </div>
                    <PaginationSection
                        show-items-per-page
                        pages-name="проектов"
                        :top-on-scroll="scrollBlock"
                        :pagination="portfolios?.meta.pagination"
                        class="mt-10" />
                </section>
                <section
                    v-else
                    class="flex h-[50vh] w-full items-center justify-center">
                    <BaseLoader />
                </section>
            </Transition>
        </LazyApiSection>

        <section class="pt-10 md:pt-20">
            <AtomHr />
        </section>

        <RemainingQuestionsSection class="py-10 md:py-20" />
    </main>
</template>

<script setup lang="ts">
import { useWindowSize } from '@vueuse/core'
import { z } from 'zod'
import type { PortfolioPage } from '~/types/pages.types'

import AtomDropdown from '~/components/atom/atom-dropdown/AtomDropdown.vue'
import AtomHr from '~/components/atom/atom-hr/AtomHr.vue'
import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import BaseLoader from '~/components/base/base-loader/BaseLoader.vue'
import TheBreadcrumbs, {
    type BreadcrumbItem,
} from '~/components/base/the-breadcrumbs/TheBreadcrumbs.vue'
import CategoryMultiselect from '~/components/particles/category-multiselect/CategoryMultiselect.vue'
import LazyApiSection from '~/components/particles/lazy-api-section/LazyApiSection.vue'
import InteractiveMap from '~/components/particles/map-section/InteractiveMap.vue'
import type { MapRegion } from '~/components/particles/map-section/map-section.types'
import PaginationSection from '~/components/particles/pagination-section/PaginationSection.vue'
import PortfolioCard from '~/components/particles/portfolio-card/PortfolioCard.vue'
import RemainingQuestionsSection from '~/components/particles/remaining-questions-section/RemainingQuestionsSection.vue'
import SelectYearPeriod from '~/components/particles/select-year-period/SelectYearPeriod.vue'

import { createPortfolioRegionSelection } from '~/utils/portfolio-region-selection'

const modalStore = useModalStore()
const nuxtApp = useNuxtApp()

const breadcrumbs: BreadcrumbItem[] = [
    {
        label: 'Главная',
        to: '/',
    },
    {
        label: 'Портфолио',
    },
]

const scheme = z.object({
    categories: z.array(z.number()).optional(),
    region_id: z.number().nullable(),
    year_from: z.string().nullable(),
    year_to: z.string().nullable(),
})

const { l5Query, l5Filters, l5UpdateQuery, l5ToQuery } = useL5(scheme, {
    fromQuery: true,
    queryMap: {
        categories: 'categories_ids[]',
        region_id: 'city.region_id',
    },
    defaults: {
        limit: 15,
    },
    ignoreKeys: ['categories', 'year_to', 'year_from'],
    include: ['all_categories', 'company_histories', 'years'],
})
const { data, error } = await useApiFetch<Page<PortfolioPage>>(
    '/v1/portfolios',
    {
        query: l5Query,
        useLastModified: true,
    }
)

if (error.value) {
    throw createError(
        Object.assign(error.value, {
            message: error.value.statusMessage,
            statusMessage: 'error',
        })
    )
}

const deferredPortfolioQuery = computed(() => ({
    ...l5Query.value,
    include: [
        'portfolios.city',
        'portfolios.categories',
        'portfolios.products',
    ].join(','),
}))

const {
    data: deferredPortfolioData,
    pending: deferredPortfolioPending,
    execute: executeDeferredPortfolios,
} = await useApiFetch<Page<PortfolioPage>>('/v1/portfolios', {
    key: 'deferred-portfolios',
    query: deferredPortfolioQuery,
    server: false,
    immediate: false,
    watch: false,
})

const { data: maps, execute: executeMaps } = await useApiFetch<
    Page<MapRegion[]>
>('/v1/maps', {
    key: 'deferred-portfolio-maps',
    server: false,
    immediate: false,
    watch: false,
    getCachedData(key) {
        return nuxtApp.payload?.data?.[key]
    },
})

const isDeferredPortfolioContentEnabled = ref(false)

async function loadDeferredPortfolioContent() {
    if (isDeferredPortfolioContentEnabled.value) return

    isDeferredPortfolioContentEnabled.value = true

    await Promise.all([executeDeferredPortfolios(), executeMaps()])
}

watch(l5Query, async () => {
    if (!isDeferredPortfolioContentEnabled.value) return

    await executeDeferredPortfolios()
})

if (data.value?.data) {
    modalStore.setFeedbackLabel(data.value.data?.feedback_label || '')
} else {
    modalStore.setFeedbackLabel('')
}

const portfolios = computed(() => {
    if (!deferredPortfolioData.value) return null

    return deferredPortfolioData.value.data.portfolios
})

const pending = computed(() => deferredPortfolioPending.value)

const { width } = useWindowSize()
const scrollTarget = ref<HTMLDivElement | null>(null)
const scrollBlock = computed<number>(() => {
    return width.value < 744
        ? Number(scrollTarget.value?.offsetTop || 0) - 52
        : width.value > 1024
          ? Number(scrollTarget.value?.offsetTop || 0) + 20
          : Number(scrollTarget.value?.offsetTop || 0) - 70
})

const regionSelection = computed(() =>
    createPortfolioRegionSelection(maps.value?.data ?? [])
)
const dropdownOptions = computed(() => regionSelection.value.options)
const selectedRegion = computed({
    get: () => regionSelection.value.toUI(l5Filters.value.region_id),
    set: (value: string | number | null) => {
        l5Filters.value.region_id = regionSelection.value.fromUI(value)
    },
})

// useL5 updates keys present in the URL; Back can remove the region key.
const route = useRoute()
watch(
    () => route.query.region_id,
    (value) => {
        if (value === undefined) {
            l5Filters.value.region_id = null
            l5Query.value = l5ToQuery(l5Filters.value)
        }
    }
)

const years = computed<number[]>(() => {
    const firstYear = 2012
    const lastYear = new Date().getFullYear()

    return [...Array(lastYear - firstYear + 1).keys()].map(
        (year) => year + firstYear
    )
})

const disabledYears = computed<number[]>(() => {
    if (!data.value?.data.years?.data?.length) return [...years.value]

    return [...years.value].filter((year) => {
        return !data.value?.data.years.data?.includes(year)
    })
})

if (data.value) {
    useSeoMeta({
        title: data.value.data.seo.seo_title,
        description: data.value.data.seo.seo_description,
        ogTitle: data.value.data.seo.og_title,
        ogDescription: data.value.data.seo.og_description,
        ogImage: data.value.data.seo.og_image
            ? data.value.data.seo.og_image
            : null,
    })

    useSchemaOrg([
        defineWebPage({
            name: data.value.data.seo.seo_title,
            inLanguage: 'ru',
            description: data.value.data.seo.seo_description,
            // mainEntity: [
            //     defineItemList({
            //         numberOfItems: data.value.data.portfolios.data.length,
            //         itemListElement: data.value.data.portfolios.data.map(
            //             (article) => {
            //                 console.log(article)
            //                 const image = article.image_main
            //                     ? article.image_main[0]
            //                     : null
            //                 return defineListItem({
            //                     item: defineArticle({
            //                         '@type': 'BlogPosting',
            //                         name: article.name,
            //                         description: stripHtml(
            //                             article.description_preview
            //                         ),
            //                         headline: article.name,
            //                         author: defineOrganization({
            //                             '@id': organization['@id'],
            //                         }),
            //                         ...(config.public.website !==
            //                             'radioguide.rent' && {
            //                             url: `https://www.${config.public.website}/portfolio/${article.slug}/`,
            //                         }),
            //                         datePublished: article.published_at,
            //                         ...(image && {
            //                             image: getMediaLink(image.url),
            //                             thumbnailUrl: getMediaLink(image.url),
            //                         }),
            //                     }),
            //                 })
            //             }
            //         ),
            //     }),
            // ],
        }),
    ])
}
</script>
